import type { FC, ReactNode } from 'react';
import React, { useContext, useCallback } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import CommentIcon from '@atlaskit/icon/core/comment';
import Button from '@atlaskit/button/standard-button';

import { ReactionLocation, ReactionContainerType } from '@confluence/reactions/entry-points/types.ts';
import { DelayedReactions } from '@confluence/reactions/entry-points/Reactions.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../Feed.tsx';

import { FeedCardContext } from './FeedCardContext.ts';

const i18n = defineMessages({
	comments: {
		id: 'feed.card.comments.label',
		defaultMessage: 'Comments',
		description: 'label for speech bubble icon that represents comments on the page',
	},
});

// Note: this component extracts the props that we don't want to send to the DOM
// This avoids styled errors about isGlobalReactionsAndLikesEnabled being passed to the DOM
/* eslint-disable @typescript-eslint/no-unused-vars */
const ReactionsWrapperBase = ({ isGlobalReactionsAndLikesEnabled, children, ...props }) => {
	return <div {...props}>{children}</div>;
};
/* eslint-enable @typescript-eslint/no-unused-vars */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReactionsWrapper = styled(ReactionsWrapperBase)<{
	isGlobalReactionsAndLikesEnabled?: boolean;
}>({
	display: 'flex',
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: (props) =>
		props.isGlobalReactionsAndLikesEnabled ? 'space-between' : 'flex-end',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentsWrapper = styled.div({
	display: 'flex',
	paddingLeft: token('space.025'),
	paddingBottom: token('space.050'),
	justifyContent: 'right',
	minWidth: '115px',
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span[role="img"]': {
		paddingRight: token('space.050'),
	},
});

export enum ShowCommentState {
	Number,
	NoShow,
	ShowComment,
	HideComment,
}

type Props = {
	numComments: number;
	contentId: string;
	contentType: string;
	spaceId?: string;
	source: string;
	showCommentState: ShowCommentState;
	onCommentToggleClick: () => void;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};

const FeedCardActionCountsComponent: FC<Props & WrappedComponentProps> = ({
	numComments,
	contentId,
	contentType,
	spaceId,
	intl,
	source,
	showCommentState,
	onCommentToggleClick,
	popularFeedTimeGranularity,
	followingFeedSortby,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { isReactionsEnabled: isGlobalReactionsAndLikesEnabled } = useContext(ReactionsContext);

	const isFollowingFeed = source === 'following-feed';

	const onToggleClick = useCallback(() => {
		onCommentToggleClick();
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId:
				showCommentState === ShowCommentState.ShowComment ? 'show-comment' : 'hide-comment',
			additionalAttributes: {
				followingFeedSortby,
			},
		});
	}, [onCommentToggleClick, fireAnalyticsEvent, showCommentState, followingFeedSortby]);

	const location =
		contentType === 'page' ? ReactionLocation.FEED_PAGE : ReactionLocation.FEED_BLOGPOST;

	const commentToggle =
		showCommentState === ShowCommentState.ShowComment ? (
			<FormattedMessage
				id="feed.comment.expand"
				defaultMessage="Show comment"
				description="Expand the comment section on feed card"
			/>
		) : (
			<FormattedMessage
				id="feed.comment.collapse"
				defaultMessage="Hide comment"
				description="Collapse the comment section on feed card"
			/>
		);

	const commentCount = !!numComments && (
		<CommentsWrapper>
			<CommentIcon label={intl.formatMessage(i18n.comments)} />
			<FormattedMessage
				id="feed.comment.count"
				defaultMessage="{numComments, plural, one {1 comment} other {# comments}}"
				description="Feed comment count label"
				values={{ numComments }}
			/>
		</CommentsWrapper>
	);

	const optionalCommentToggle = showCommentState > ShowCommentState.NoShow && (
		<Button
			onClick={onToggleClick}
			appearance="subtle-link"
			spacing="none"
			iconBefore={<CommentIcon label={intl.formatMessage(i18n.comments)} />}
		>
			{commentToggle}
		</Button>
	);

	const renderComments =
		showCommentState == ShowCommentState.Number ? commentCount : optionalCommentToggle;

	const renderReactionsAndComments = (): ReactNode | null => {
		if (contentId && contentType && spaceId) {
			if (isFollowingFeed) {
				if (
					expValEquals(
						'confluence_following_feed_remove_social_proof',
						'cohort',
						'remove-emojis-and-comments-counts',
					)
				) {
					return null;
				}

				if (
					expValEquals('confluence_following_feed_remove_social_proof', 'cohort', 'remove-emojis')
				) {
					return <>{renderComments}</>;
				}
			}

			return (
				<>
					<ReactionsWrapper
						isGlobalReactionsAndLikesEnabled={isGlobalReactionsAndLikesEnabled}
						data-testid="feed-content-reactions"
					>
						{isGlobalReactionsAndLikesEnabled && (
							<DelayedReactions
								contentId={contentId}
								location={location}
								containerId={spaceId}
								containerType={ReactionContainerType.SPACE}
								popularFeedTimeGranularity={popularFeedTimeGranularity}
								limit={undefined}
								sort={undefined}
							/>
						)}
						{renderComments}
					</ReactionsWrapper>
				</>
			);
		}

		return null;
	};

	return <small>{renderReactionsAndComments()}</small>;
};

export const FeedCardActionCounts = injectIntl(FeedCardActionCountsComponent);
